
import { GroupCatalogFilters, GroupEntity } from '@/types/catalogs/Groups'
import { defineComponent } from 'vue'
import { ComposeMessage, NotificationRequest, NOTIFICATION_TARGET } from '@/types/notifications/Notification'
import userInfoStore from '@/store/userInfo'
import CatalogsService from '@/services/CatalogsService'
import { PageRequest } from '@/types/Support'
import { Domain, DomainCatalogFilters, User, UserCatalogFilters } from '@/types/Catalogs'
import { PermissionNames, UserDomain } from '@/types/Auth'
import { NUMBER, STRING } from '@/types/GeneralConstants'
import NotificationService from '@/services/notifications/NotificationService'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import NotAllowedView from '@/components/permissions/NotAllowedView.vue'
import { NOT_ALLOWED_VIEW } from '@/types/viewAccess/AccessFailed'

function intiComponentValues (): ComposeMessage {
  const SEND_TO = NOTIFICATION_TARGET
  const users: User[] = []
  const groups: GroupEntity[] = []
  const domains: Domain[]|UserDomain[] = []
  return {
    NOT_ALLOWED_VIEW: NOT_ALLOWED_VIEW,
    settingsCollapsed: false,
    SEND_TO: SEND_TO,
    sendTo: SEND_TO.USERS,
    users: users,
    selectedUsers: [],
    search: '',
    loadingUsers: false,
    domains: domains,
    selectedDomain: -1,
    loadingDomains: false,
    groups: groups,
    loadingGroups: false,
    selectedGroups: [],
    openSelection: false,
    outOfSearch: true,
    outOfSelection: true,
    confirmDomainChange: false,
    filteredItems: [],
    subgroups: false,
    subject: '',
    message: '',
    sendingMessage: false,
    messageSended: false
  }
}

export default defineComponent({
  components: {
    NotAllowedView
  },
  data (): ComposeMessage {
    return intiComponentValues()
  },
  created () {
    this.initComponentOptions()
  },
  watch: {
    search () {
      this.filterItems()
    },
    selectedDomain (domain: number, lastDomain: number) {
      this.handleDomainChange(domain)
    },
    sendTo () {
      this.turnOffMessageSendedFlag()
      this.clearSelectedUsers()
      this.clearSelectedGroups()
      this.closeItemSelection()
      if (this.isUserInRootDomain) {
        this.handleDomainChange(this.selectedDomain)
      }
      if (!this.isUserInRootDomain) this.selectedDomain = userInfoStore.userInfo.domain.id
      this.filterItems()
    }
  },
  computed: {
    sendToUsers (): boolean {
      return this.sendTo === this.SEND_TO.USERS
    },
    sendToGroups (): boolean {
      return this.sendTo === this.SEND_TO.GROUPS
    },
    sendToDomain (): boolean {
      return this.sendTo === this.SEND_TO.DOMAIN
    },
    isUserInRootDomain (): boolean {
      return userInfoStore.inRootDomain()
    },
    permissionToComposeMessage (): boolean {
      return userInfoStore.hasPermission(PermissionNames.COMPOSE_MESSAGE_VIEW)
    },
    permissionToReadUsers (): boolean {
      return userInfoStore.hasPermission(PermissionNames.USERS_READ)
    },
    permissionToReadGroups (): boolean {
      return userInfoStore.hasPermission(PermissionNames.GROUPS_READ)
    },
    permissionToReadDomains (): boolean {
      return userInfoStore.hasPermission(PermissionNames.DOMAINS_READ)
    },
    existsUsersSelected (): boolean {
      return this.sendToUsers && this.selectedUsers.length > 0
    },
    existsGroupsSelected (): boolean {
      return this.sendToGroups && this.selectedGroups.length > 0
    },
    existsUsersOrGroupsSelected (): boolean {
      return this.existsUsersSelected || this.existsGroupsSelected
    },
    existsDomainSelected (): boolean {
      return this.sendToDomain && this.selectedDomain !== -1
    },
    existsTarget (): boolean {
      return this.existsDomainSelected || this.existsUsersOrGroupsSelected
    },
    existsSubject (): boolean {
      return this.subject !== undefined && this.subject !== null && this.subject.length > 0
    },
    existsMessage (): boolean {
      return this.message !== undefined && this.message !== null && this.message.length > 0
    },
    existsMessageContent (): boolean {
      return this.existsSubject && this.existsMessage
    }
  },
  methods: {
    handleDomainChange (domain: number) {
      this.selectedDomain = domain
      if (this.sendToUsers &&
        this.isUserInRootDomain &&
        this.permissionToReadUsers &&
        domain > NUMBER.MINUS_ONE
      ) {
        this.users = []
        this.clearSelectedUsers()
        this.loadUsers(this.prepareUsersRequest())
      }
      if (this.sendToGroups &&
        this.isUserInRootDomain &&
        this.permissionToReadGroups &&
        domain > NUMBER.MINUS_ONE
      ) {
        this.groups = []
        this.clearSelectedGroups()
        this.loadGroups(this.prepareGroupRequest())
      }
      this.closeDomainChangeConfirmation()
    },
    clearSelectedUsers () {
      this.selectedUsers = []
    },
    clearSelectedGroups () {
      this.selectedGroups = []
    },
    clearSelectedDomain () {
      this.selectedDomain = NUMBER.MINUS_ONE
      if (!this.isUserInRootDomain) this.selectedDomain = userInfoStore.userInfo.domain.id
    },
    clearMessageContent () {
      this.subject = STRING.EMPTY_STRING
      this.message = STRING.EMPTY_STRING
    },
    resetComponentValues () {
      this.clearSearchFilter()
      this.clearSelectedUsers()
      this.clearSelectedGroups()
      this.clearSelectedDomain()
      this.clearMessageContent()
    },
    initComponentOptions (): void {
      if (this.permissionToComposeMessage) {
        if (this.permissionToReadUsers) {
          this.loadUsers(this.prepareUsersRequest())
        }
        if (this.permissionToReadGroups) {
          this.loadGroups(this.prepareGroupRequest())
        }
        if (this.permissionToReadDomains && this.isUserInRootDomain) {
          this.loadDomains(this.prepareDomainRequest())
        } else {
          this.addUserDomainInDomains()
        }
      }
    },
    addUserDomainInDomains () {
      this.domains.push(userInfoStore.userInfo.domain)
    },
    prepareDomainRequest () {
      const request = new PageRequest<DomainCatalogFilters>()
      return request
    },
    loadDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
      this.loadingDomains = true
      CatalogsService.getDomains(pageRequest)
        .then((response) => {
          this.domains = this.domains.concat(response.results)
          if (response.total > this.domains.length) {
            pageRequest.page++
            this.loadDomains(pageRequest)
          }
        })
        .finally(() => { this.loadingGroups = false })
    },
    prepareUsersRequest () {
      const request = new PageRequest<UserCatalogFilters>()
      if (this.isUserInRootDomain && !this.sendToDomain) request.filters.domainId = this.selectedDomain
      request.sort = 'name'
      return request
    },
    async loadUsers (pageRequest: PageRequest<UserCatalogFilters>) {
      this.loadingUsers = true
      await CatalogsService.getUsers(pageRequest)
        .then((response) => {
          this.users = this.users.concat(response.results)
          if (response.total > this.users.length) {
            pageRequest.page++
            this.loadUsers(pageRequest)
          }
        })
        .then(() => {
          this.filterItems()
        })
        .finally(() => { this.loadingGroups = false })
    },
    prepareGroupRequest () {
      const request = new PageRequest<GroupCatalogFilters>()
      if (this.isUserInRootDomain && !this.sendToDomain) request.filters.domain = this.selectedDomain
      return request
    },
    existGroupInGroups (group: GroupEntity, groups: GroupEntity[]): boolean {
      return groups.findIndex((base) => base.id === group.id) > NUMBER.MINUS_ONE
    },
    addGroupResults (results: GroupEntity[]) {
      for (const group of results) {
        if (!this.existGroupInGroups(group, this.groups)) this.groups.push(group)
        this.addGroupResults(group.subGroups)
      }
    },
    async loadGroups (pageRequest: PageRequest<GroupCatalogFilters>) {
      this.loadingGroups = true
      await CatalogsService.getGroups(pageRequest)
        .then((response) => {
          this.addGroupResults(response.results)
        })
        .then(() => {
          this.filterItems()
        })
        .finally(() => { this.loadingGroups = false })
    },
    clearSearchFilter (): void {
      this.search = STRING.EMPTY_STRING
      this.closeItemSelection()
    },
    openItemSelection () {
      this.openSelection = true
    },
    closeItemSelection () {
      this.openSelection = false
    },
    closeItemSelectionValidate () {
      if (this.outOfSearch && this.outOfSelection) {
        this.openSelection = false
      }
    },
    outOfSearchBar () {
      this.outOfSearch = true
    },
    inOfSearchBar () {
      this.outOfSearch = false
    },
    outOfSelectionList () {
      this.outOfSearch = true
    },
    inOfSelectionList () {
      this.outOfSearch = false
    },
    openDomainChangeConfirmation () {
      this.confirmDomainChange = true
    },
    closeDomainChangeConfirmation () {
      this.confirmDomainChange = false
    },
    findIndexInList (list: User[] | GroupEntity[]) {
      return (item: User | GroupEntity) => list.findIndex((it) => it.id === item.id) > NUMBER.MINUS_ONE
    },
    userAlreadySelected (user: User): boolean {
      return this.findIndexInList(this.selectedUsers)(user)
    },
    groupAlreadySelected (group: GroupEntity): boolean {
      return this.findIndexInList(this.selectedGroups)(group)
    },
    itemAlreadySelected (item: User | GroupEntity) {
      if (this.sendToUsers) return this.userAlreadySelected(item as User)
      if (this.sendToGroups) return this.groupAlreadySelected(item as GroupEntity)
      return false
    },
    addItem (item: User | GroupEntity): void {
      if (this.sendToUsers) {
        if (!this.userAlreadySelected(item as User)) {
          this.selectedUsers.push(item as User)
        }
      }
      if (this.sendToGroups) {
        if (!this.groupAlreadySelected(item as GroupEntity)) {
          this.selectedGroups.push(item as GroupEntity)
        }
      }
      this.focusItemSelector()
    },
    focusItemSelector () {
      const input = document.getElementById('itemSelector')
      input?.focus()
    },
    removeItemFromList (list: User[] | GroupEntity[]) {
      return (item: User | GroupEntity) => {
        const index = list.findIndex(it => it.id === item.id)
        if (index >= 0) list.splice(index, 1)
      }
    },
    removeSelectedUser (user: User) {
      const removeUser = this.removeItemFromList(this.selectedUsers)
      removeUser(user)
    },
    removeSelectedGroup (group: GroupEntity) {
      const removeGroup = this.removeItemFromList(this.selectedGroups)
      removeGroup(group)
    },
    removeItem (item: User | GroupEntity) {
      if (this.sendToUsers) this.removeSelectedUser(item as User)
      if (this.sendToGroups) this.removeSelectedGroup(item as GroupEntity)
    },
    findItemInList (list: any[]) {
      return (id: number) => list.find(item => item.id === id)
    },
    getUserSelectedById (id: number): User | undefined {
      const getUser = this.findItemInList(this.selectedUsers)
      return getUser(id)
    },
    getGroupSelectedById (id: number): User | undefined {
      const getGroup = this.findItemInList(this.selectedGroups)
      return getGroup(id)
    },
    getItemSelected (id: number) {
      if (this.sendToUsers) return this.getUserSelectedById(id)
      if (this.sendToGroups) return this.getGroupSelectedById(id)
      return undefined
    },
    selectedItems (): User[] | GroupEntity[] {
      if (this.sendToUsers) return this.selectedUsers
      if (this.sendToGroups) return this.selectedGroups
      return []
    },
    filterList (items: any[], filter: string): any[] {
      const search = new RegExp(filter)
      if (!filter) return items
      return items.filter(item => this.matchName(search, item.name))
    },
    matchName (regExp: RegExp, name: string) {
      return regExp.test(name)
    },
    filterItems () {
      if (this.sendToUsers) this.filteredItems = this.filterList(this.users, this.search)
      else if (this.sendToGroups) this.filteredItems = this.filterList(this.groups, this.search)
      else this.filteredItems = []
    },
    prepareNotificationRequest (): NotificationRequest {
      const request = new NotificationRequest()
      request.subject = this.subject
      request.message = this.message
      if (this.sendToUsers) {
        request.users = this.selectedUsers.map(it => it.id)
      } else if (this.sendToGroups) {
        request.groups = this.selectedGroups.map(it => it.id)
        if (this.subgroups) request.subGroups = this.subgroups
      } else {
        if (this.sendToDomain) request.domain = this.selectedDomain
      }
      return request
    },
    sendMessage () {
      this.sendingMessage = true
      NotificationService.sendNotification(this.prepareNotificationRequest())
        .then(() => {
          this.messageSended = true
          this.resetComponentValues()
        })
        .catch((error) => {
          new MessageError({
            propsData: {
              title: 'Error al enviar el mensaje',
              message: '',
              delay: 10000,
              error: error } }).displayMessage()
        })
        .finally(() => {
          this.sendingMessage = false
        })
    },
    turnOffMessageSendedFlag () {
      this.messageSended = false
    }
  }
})
